import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import * as Popover from '@radix-ui/react-popover';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Columns3, Filter, Inbox, Plus, RotateCcw, Rows3, Rows4, Save, Search, Trash2, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import type { FilterFieldMeta, FilterGroup, FilterNode, ListResponse, SortSpec, TicketSummary } from '@aurora/contracts';
import { isFilterGroup } from '@aurora/contracts';
import { post, get, type ApiError } from '../lib/api';
import { cn } from '../lib/cn';
import { pluralise } from '../lib/format';
import { useStatuses } from '../lib/directory';
import { useSession } from '../lib/session';
import { Badge, Button, EmptyState, ErrorState, Input } from '../components/ui';
import { useToast } from '../components/toast';
import { PageHeader } from './shell';
import { BulkActionBar } from '../features/tickets/bulk-actions';
import { describeCondition } from '../features/tickets/filter-panel';
import { ListFilterBuilder } from '../features/tickets/list-builder';
import { invalidateTicket, ticketKeys } from '../features/tickets/list-cache';
import { rememberPerson, useReferenceLabeller } from '../features/tickets/list-filter-values';
import {
  asFilterNode,
  asSortSpecs,
  combineFilters,
  countFilterConditions,
  describeSort,
  EMPTY_GROUP,
  parseFilterParam,
  parseQuickParam,
  parseRequesterShow,
  parseSortParam,
  QUICK_DIMENSION_LABELS,
  quickFilterNodes,
  quickFiltersFor,
  REQUESTER_SHOW,
  SERVER_DEFAULT_SORT,
  serialiseFilter,
  serialiseSort,
  type QuickFilter,
  type RequesterShow,
} from '../features/tickets/list-filters';
import { ListPagination, PER_PAGE_CHOICES } from '../features/tickets/list-pagination';
import { isRequesterView, seesOnlyAssigned } from '../features/tickets/list-scope';
import { ticketNumberIn, useDebouncedSearch } from '../features/tickets/list-search';
import { useQuickCounts } from '../features/tickets/quick-counts';
import {
  ALL_COLUMNS,
  DEFAULT_COLUMN_KEYS,
  sanitiseColumnKeys,
  TicketTable,
  type ColumnKey,
  type Density,
} from '../features/tickets/ticket-table';
import { SaveViewDialog, useSaveViewChanges, type ViewSnapshot } from '../features/tickets/views-dialogs';
import { canEditView, useSavedViews, type SavedView } from '../features/tickets/views-api';
import { ViewTabs, type ActiveList } from '../features/tickets/views-menu';
import { ListShortcuts } from '../features/tickets/list-shortcuts';

/**
 * The ticket list.
 *
 * Everything that shapes the list lives in the URL — the view, the builder's
 * conditions, the quick chips, the search, the sort and the page — so any list
 * an agent builds is a link they can paste to a colleague and get exactly what
 * they were looking at.
 *
 * A saved view is the starting point, not a lock: its conditions show in the
 * builder and as chips, and anything changed on top of it can be saved back to
 * it or as a view of its own. The chips and the builder always narrow the list
 * together; see `list-filters.ts` for why that needed saying.
 *
 * Somebody who raises tickets rather than working them gets a different page
 * altogether: "My requests", in their own words.
 */
export function TicketsRoute() {
  const { session } = useSession();
  // Which page this is depends on who is looking; the shell only renders once
  // the session is known, so this is a guard rather than a loading state.
  if (!session) return null;
  if (isRequesterView(session)) return <RequesterTicketList />;
  return <AgentTicketList />;
}

// ---------------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------------

function parsePage(raw: string | null): number {
  const page = Math.trunc(Number(raw ?? '1'));
  return Number.isFinite(page) && page >= 1 ? page : 1;
}

const COLUMNS_KEY = 'aurora.ticketColumns';
const PER_PAGE_KEY = 'aurora.ticketsPerPage';
const DENSITY_KEY = 'aurora.ticketDensity';

/** Row spacing is this browser's preference; unreadable storage means the roomy default. */
function readDensity(): Density {
  try {
    return localStorage.getItem(DENSITY_KEY) === 'compact' ? 'compact' : 'comfortable';
  } catch {
    return 'comfortable';
  }
}

function writeDensity(value: Density): void {
  try {
    localStorage.setItem(DENSITY_KEY, value);
  } catch {
    // Not remembered; still applied for this visit.
  }
}

/** Column choice is a convenience: unreadable or unwritable storage is not an error. */
function readStoredColumns(): ColumnKey[] {
  try {
    const stored = localStorage.getItem(COLUMNS_KEY);
    if (stored) return sanitiseColumnKeys(JSON.parse(stored));
  } catch {
    // Fall through to the default set.
  }
  return DEFAULT_COLUMN_KEYS;
}

function writeStoredColumns(keys: ColumnKey[]): void {
  try {
    localStorage.setItem(COLUMNS_KEY, JSON.stringify(keys));
  } catch {
    // Not persisted; still applied for this visit.
  }
}

function readPerPage(): number {
  try {
    const stored = Number(localStorage.getItem(PER_PAGE_KEY));
    if ((PER_PAGE_CHOICES as readonly number[]).includes(stored)) return stored;
  } catch {
    // Fall through.
  }
  return PER_PAGE_CHOICES[0];
}

function writePerPage(value: number): void {
  try {
    localStorage.setItem(PER_PAGE_KEY, String(value));
  } catch {
    // Not persisted.
  }
}

/** A URL updater that resets the page unless told otherwise. */
function useParamUpdater() {
  const [, setSearchParams] = useSearchParams();
  return useCallback(
    (changes: Record<string, string | null>, options: { keepPage?: boolean } = {}) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
          }
          if (!options.keepPage) next.delete('page');
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );
}

/** '/' puts the cursor in the search box, from anywhere on the page that is not already a field. */
function useSlashToSearch(input: RefObject<HTMLInputElement | null>) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select, [contenteditable="true"], [role="dialog"], [role="menu"]')) return;
      event.preventDefault();
      input.current?.focus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [input]);
}

function SearchBox({
  inputRef,
  value,
  onChange,
  onFlush,
  placeholder,
  label,
}: {
  inputRef: RefObject<HTMLInputElement | null>;
  value: string;
  onChange: (next: string) => void;
  onFlush: () => void;
  placeholder: string;
  label: string;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const number = ticketNumberIn(value);
  return (
    <div className="relative min-w-56 flex-1">
      <Search
        aria-hidden
        className="pointer-events-none absolute top-1/2 start-2.5 size-3.5 -translate-y-1/2 text-ink-subtle"
      />
      <Input
        ref={inputRef}
        type="search"
        value={value}
        maxLength={512}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          // Somebody who types a ticket's number wants that ticket — and
          // "Back to tickets" there returns to this list, as a row does.
          if (number !== null) navigate(`/tickets/${number}`, { state: { from: `${location.pathname}${location.search}` } });
          else onFlush();
        }}
        placeholder={placeholder}
        aria-label={label}
        aria-keyshortcuts="/"
        aria-describedby={number !== null ? 'ticket-search-jump' : undefined}
        // The box follows the direction of what is typed (an Arabic search
        // reads right to left) while the icon stays where the page put it, so
        // the room for the icon is kept on both sides: padding on the start
        // side alone moved to the far end for Arabic, and the text ran under
        // the icon.
        className="px-8"
      />
      {number !== null && (
        <span
          id="ticket-search-jump"
          className="pointer-events-none absolute top-1/2 end-2 hidden -translate-y-1/2 rounded bg-sunken px-1.5 text-mini text-ink-muted sm:block"
        >
          Enter opens #{number}
        </span>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The agent's list
// ---------------------------------------------------------------------------

const SERVER_FILTER_ERRORS = new Set(['invalid_filter', 'validation_failed', 'malformed_request']);

function asGroup(node: FilterNode | null): FilterGroup {
  if (!node) return EMPTY_GROUP;
  return isFilterGroup(node) ? node : { logic: 'and', nodes: [node] };
}

/** The reference lookups the builder's conditions need, to name values in chips. */
function referencesIn(group: FilterGroup, fields: Map<string, FilterFieldMeta>, out = new Set<string>()): Set<string> {
  for (const node of group.nodes) {
    if (isFilterGroup(node)) referencesIn(node, fields, out);
    else {
      const references = fields.get(node.field)?.references;
      if (references) out.add(references);
    }
  }
  return out;
}

function AgentTicketList() {
  const { session, can } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const update = useParamUpdater();
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToSearch(searchRef);

  const assignedOnly = seesOnlyAssigned(session);
  const chips = useMemo(() => quickFiltersFor({ assignedOnly }), [assignedOnly]);
  const canTrash = can('ticket.delete');

  // --- What the URL says ----------------------------------------------------
  const viewParam = searchParams.get('view');
  const filterParam = searchParams.get('filter');
  const quickParam = searchParams.get('quick');
  const qParam = searchParams.get('q');
  const sortParam = searchParams.get('sort');
  const page = parsePage(searchParams.get('page'));

  const trash = viewParam === 'trash' && canTrash;
  const viewId = viewParam && /^\d+$/.test(viewParam) ? Number(viewParam) : null;

  const viewsQuery = useSavedViews();
  const views = useMemo(() => viewsQuery.data ?? [], [viewsQuery.data]);
  const activeView = viewId !== null ? (views.find((view) => view.id === viewId) ?? null) : null;
  // Until the views arrive, a `?view=` list cannot know its own filter; asking
  // for the list without it would flash every ticket first.
  const viewPending = viewId !== null && viewsQuery.isLoading;
  const viewMissing = viewId !== null && viewsQuery.isSuccess && activeView === null;
  const active: ActiveList = trash ? { kind: 'trash' } : activeView ? { kind: 'view', view: activeView } : { kind: 'all' };

  const viewFilter = useMemo(() => (activeView ? asFilterNode(activeView.filter) : null), [activeView]);
  /** The builder's conditions: the view's, unless changed on top of it. */
  const builder = useMemo<FilterGroup>(
    () => (filterParam !== null ? parseFilterParam(filterParam) : asGroup(viewFilter)),
    [filterParam, viewFilter],
  );
  const quick = useMemo(() => parseQuickParam(quickParam, chips), [quickParam, chips]);
  const viewQ = activeView?.search_query ?? '';
  const q = qParam ?? viewQ;
  const viewSort = useMemo(() => (activeView ? asSortSpecs(activeView.sort) : []), [activeView]);
  const explicitSort = useMemo(() => parseSortParam(sortParam), [sortParam]);
  const requestSort = explicitSort.length > 0 ? explicitSort : viewSort;
  const implicitSort = viewSort.length > 0 ? viewSort : SERVER_DEFAULT_SORT;

  const effectiveFilter = useMemo(
    () => combineFilters([builder.nodes.length > 0 ? builder : null, ...quickFilterNodes(quick, chips)]),
    [builder, quick, chips],
  );

  // --- Local state ------------------------------------------------------------
  const [perPage, setPerPage] = useState(readPerPage);
  const [panelOpen, setPanelOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const [restoring, setRestoring] = useState<ReadonlySet<number>>(() => new Set());
  const [storedColumns, setStoredColumns] = useState<ColumnKey[]>(readStoredColumns);
  const [columnOverride, setColumnOverride] = useState<{ key: string; columns: ColumnKey[] } | null>(null);
  const [density, setDensity] = useState<Density>(readDensity);
  const toggleDensity = () => {
    const next: Density = density === 'compact' ? 'comfortable' : 'compact';
    setDensity(next);
    writeDensity(next);
  };

  // A selection belongs to the list it was made in. Kept across pages and
  // sorting, but a new filter, search or view starts again: bulk actions used
  // to reach tickets the agent could no longer see.
  const selectionScope = JSON.stringify([viewParam, filterParam, quickParam, qParam]);
  useEffect(() => {
    setSelected((current) => (current.size === 0 ? current : new Set()));
  }, [selectionScope]);

  // --- Data -------------------------------------------------------------------
  const metaQuery = useQuery({
    queryKey: ['ticket-filter-meta'],
    queryFn: () => get<{ resource: string; fields: FilterFieldMeta[] }>('/tickets/filters'),
    staleTime: 5 * 60_000,
  });
  const fields = useMemo(() => metaQuery.data?.fields ?? [], [metaQuery.data]);
  const fieldsByKey = useMemo(() => new Map(fields.map((field) => [field.field, field])), [fields]);

  const statuses = useStatuses(can('ticket.edit'));

  const search = q.trim();
  const listQuery = useQuery<ListResponse<TicketSummary>, ApiError>({
    queryKey: ticketKeys.list({ filter: effectiveFilter, q: search, sort: requestSort, page, perPage, trash }),
    queryFn: () =>
      post<ListResponse<TicketSummary>>('/tickets/search', {
        ...(effectiveFilter ? { filter: effectiveFilter } : {}),
        ...(search ? { q: search } : {}),
        ...(requestSort.length > 0 ? { sort: requestSort } : {}),
        ...(trash ? { trash: true } : {}),
        page,
        per_page: perPage,
      }),
    // The previous page stays up while the next loads, so the table does not
    // collapse to a spinner on every keystroke and page change.
    placeholderData: keepPreviousData,
    // A queue left open on a second screen keeps itself current.
    refetchInterval: 60_000,
    enabled: !viewPending && !viewMissing,
  });

  const tickets = useMemo(() => listQuery.data?.data ?? [], [listQuery.data]);
  const meta = listQuery.data?.meta;

  // What each quick filter would match here, beside its name.
  const chipCounts = useQuickCounts({
    base: builder.nodes.length > 0 ? builder : null,
    quick,
    chips,
    q: search,
    viewer: { me: session?.user.id ?? null, groupIds: (session?.groups ?? []).map((group) => group.id) },
    // The trash is not what the chips count, and a view not yet known has no conditions to count within.
    enabled: !trash && !viewPending && !viewMissing,
  });

  // A page emptied from under the reader — its last tickets resolved in bulk —
  // steps back to the last page that has any, rather than reporting that
  // nothing matches while the total says otherwise.
  useEffect(() => {
    if (!meta || listQuery.isPlaceholderData) return;
    if (meta.total > 0 && meta.page > meta.total_pages) update({ page: String(meta.total_pages) }, { keepPage: true });
  }, [meta, listQuery.isPlaceholderData, update]);

  // Requesters seen in the list can then be named on a chip that filters by them.
  useEffect(() => {
    for (const ticket of tickets) if (ticket.requester) rememberPerson(ticket.requester.id, ticket.requester.name);
  }, [tickets]);

  const references = useMemo(() => referencesIn(builder, fieldsByKey), [builder, fieldsByKey]);
  const labelReference = useReferenceLabeller({
    agents: references.has('agents') || references.has('users'),
    groups: references.has('groups'),
    departments: references.has('departments'),
    locations: references.has('locations'),
  });

  // --- Columns ----------------------------------------------------------------
  // A view remembers its own columns; changes on top of it are held for this
  // visit and saved with the view. Without a view they are a personal setting.
  const columnsKey = trash ? 'trash' : activeView ? `view-${activeView.id}` : 'all';
  const viewColumns = activeView?.columns?.length ? sanitiseColumnKeys(activeView.columns) : null;
  const columnsChanged = columnOverride?.key === columnsKey;
  const columnKeys = columnsChanged ? columnOverride!.columns : (viewColumns ?? storedColumns);
  const columns = useMemo(
    () => ALL_COLUMNS.filter((column) => columnKeys.includes(column.key)),
    [columnKeys],
  );
  const setColumns = (keys: ColumnKey[]) => {
    if (activeView) setColumnOverride({ key: columnsKey, columns: keys });
    else {
      setStoredColumns(keys);
      writeStoredColumns(keys);
    }
  };
  const resetColumns = () => {
    if (activeView) setColumnOverride(null);
    else setColumns(DEFAULT_COLUMN_KEYS);
  };

  // --- Changing the list ---------------------------------------------------------
  const setBuilder = (group: FilterGroup) => {
    const serial = serialiseFilter(group);
    if (!activeView) {
      update({ filter: serial });
      return;
    }
    // Back to exactly what the view saved: drop the override, so the list is
    // "the view" again rather than an edited copy that happens to match.
    const saved = serialiseFilter(asGroup(viewFilter));
    update({ filter: serial === saved ? null : (serial ?? 'none') });
  };

  const setQ = (next: string) => {
    // Over a view with a search of its own, an empty box means "no search",
    // so the empty value is kept rather than falling back to the view's.
    update({ q: next === viewQ ? null : next });
  };
  const [searchDraft, setSearchDraft, flushSearch] = useDebouncedSearch(q, setQ);

  const toggleQuick = (id: string) => {
    const next = new Set(quick);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    update({ quick: [...next].join(',') || null });
  };

  const changed = filterParam !== null || quick.size > 0 || qParam !== null || sortParam !== null || columnsChanged;
  const anythingApplied = builder.nodes.length > 0 || quick.size > 0 || search.length > 0 || explicitSort.length > 0;

  const reset = () => {
    setColumnOverride(null);
    update({ filter: null, quick: null, q: null, sort: null });
  };

  // --- Saving views ---------------------------------------------------------------
  const canCreateViews = can('view.create_personal') || can('view.create_shared');
  const editableView =
    activeView && canEditView(activeView, can) && (activeView.visibility === 'private' || activeView.visibility === 'shared')
      ? activeView
      : null;
  const snapshot: ViewSnapshot = {
    filter: effectiveFilter,
    sort: requestSort,
    searchQuery: search || null,
    columns: columnKeys,
  };
  const { save: saveChanges, pending: savingChanges } = useSaveViewChanges();
  const offerSave = !trash && (activeView ? changed : anythingApplied);
  // The server can change a view's conditions but has no way to remove them
  // all: a save that left them out kept the old ones, under a toast saying
  // the changes were saved.
  const saveBackBlocked =
    activeView && viewFilter !== null && effectiveFilter === null
      ? 'A view’s conditions can be changed, not all removed. Save this as a new view instead.'
      : null;

  // --- Rows ---------------------------------------------------------------------------
  const categoryByKey = useMemo(
    () => new Map((statuses.data ?? []).map((status) => [status.key, status.category])),
    [statuses.data],
  );
  const choicesFor = (field: string) =>
    (fieldsByKey.get(field)?.choices ?? []).map((choice) => ({ value: String(choice.value), label: choice.label }));
  const statusChoices = choicesFor('status').map((choice) => ({ ...choice, category: categoryByKey.get(choice.value) }));

  const selectable = trash ? canTrash : can('ticket.bulk_edit');

  const restoreOne = async (ticket: TicketSummary) => {
    setRestoring((current) => new Set(current).add(ticket.number));
    try {
      await post(`/tickets/${ticket.number}/restore`);
      toast.success(`Restored #${ticket.number}`, ticket.subject);
      invalidateTicket(queryClient, ticket.number);
      setSelected((current) => {
        if (!current.has(ticket.number)) return current;
        const next = new Set(current);
        next.delete(ticket.number);
        return next;
      });
    } catch (error) {
      toast.error(error);
    } finally {
      setRestoring((current) => {
        const next = new Set(current);
        next.delete(ticket.number);
        return next;
      });
    }
  };

  const builderCount = countFilterConditions(builder);
  const title = trash ? 'Trash' : 'Tickets';
  const description = trash
    ? 'Deleted tickets are hidden from every list until somebody restores them.'
    : viewMissing
      ? 'This view is not available.'
      : meta && !listQuery.isPlaceholderData
      ? `${pluralise(meta.total, 'ticket')}${activeView ? ` in ${activeView.name}` : ''}`
      : 'Loading…';

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={title}
        description={description}
        // "New ticket" lives in the top bar, directly above; a second primary
        // button with the same words here was the same action twice.
      >
        <ViewTabs
          views={views}
          viewsState={viewsQuery.isLoading ? 'loading' : viewsQuery.isError ? 'error' : 'ready'}
          active={active}
          modified={changed}
          canTrash={canTrash}
          onRetry={() => void viewsQuery.refetch()}
        />

        {/* --- Search and controls ------------------------------------------- */}
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <SearchBox
            inputRef={searchRef}
            value={searchDraft}
            onChange={setSearchDraft}
            onFlush={flushSearch}
            placeholder="Search subjects, descriptions and replies — or type #number"
            label="Search tickets"
          />

          <Button
            variant={panelOpen || builderCount > 0 ? 'primary' : 'secondary'}
            onClick={() => setPanelOpen((open) => !open)}
            aria-expanded={panelOpen}
            aria-controls="ticket-filter-panel"
            aria-label={builderCount > 0 ? `Filters, ${builderCount} applied` : 'Filters'}
          >
            <Filter aria-hidden className="size-3.5" />
            Filters
            {builderCount > 0 && (
              <span aria-hidden className="ms-0.5 rounded-full bg-white/25 px-1.5 text-mini font-semibold">
                {builderCount}
              </span>
            )}
          </Button>

          <ColumnPicker
            selected={columnKeys}
            onChange={setColumns}
            onReset={resetColumns}
            scope={activeView ? 'view' : 'mine'}
          />

          <Button
            size="icon"
            variant={density === 'compact' ? 'subtle' : 'secondary'}
            aria-pressed={density === 'compact'}
            aria-label="Compact rows"
            title={density === 'compact' ? 'Compact rows are on. Click for roomier rows.' : 'Compact rows: fit more tickets on the screen.'}
            onClick={toggleDensity}
          >
            {density === 'compact' ? (
              <Rows4 aria-hidden className="size-3.5" />
            ) : (
              <Rows3 aria-hidden className="size-3.5" />
            )}
          </Button>

          {!trash && <ListShortcuts />}

          {offerSave && canCreateViews && (
            <SaveViewControl
              view={editableView && changed ? editableView : null}
              blocked={saveBackBlocked}
              saving={savingChanges}
              onSaveNew={() => setSaveOpen(true)}
              onSaveChanges={(view) =>
                void saveChanges(view, snapshot, () => {
                  setColumnOverride(null);
                  navigate(`/tickets?view=${view.id}`, { replace: true });
                })
              }
            />
          )}

          {(activeView ? changed : anythingApplied) && (
            <Button variant="ghost" onClick={reset}>
              <RotateCcw aria-hidden className="size-3.5" />
              {activeView ? 'Undo changes' : 'Clear all'}
            </Button>
          )}
        </div>

        {/* --- Quick chips ------------------------------------------------------ */}
        <QuickChips chips={chips} active={quick} onToggle={toggleQuick} counts={chipCounts} />

        {/* --- What the builder is filtering by ---------------------------------- */}
        {builder.nodes.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <span className="text-mini text-ink-subtle">{activeView && filterParam === null ? 'View filter:' : 'Filtered by:'}</span>
            {builder.nodes.map((node, index) => {
              const label = isFilterGroup(node)
                ? `${node.logic === 'or' ? 'Any of' : 'All of'}: ${node.nodes
                    .map((child) => (isFilterGroup(child) ? '…' : describeCondition(child, fieldsByKey, labelReference)))
                    .join(node.logic === 'or' ? ' or ' : ' and ')}`
                : describeCondition(node, fieldsByKey, labelReference);
              return (
                <Chip
                  key={index}
                  label={label}
                  onRemove={() => setBuilder({ ...builder, nodes: builder.nodes.filter((_, i) => i !== index) })}
                />
              );
            })}
          </div>
        )}
      </PageHeader>

      {/* --- The builder ------------------------------------------------------------ */}
      {panelOpen && (
        <div id="ticket-filter-panel">
          {metaQuery.isLoading ? (
            <p className="border-b border-line bg-sunken/60 px-4 py-3 text-body text-ink-muted sm:px-6">
              Loading the fields you can filter by…
            </p>
          ) : metaQuery.isError ? (
            <div
              role="alert"
              className="flex items-center gap-3 border-b border-line bg-sunken/60 px-4 py-3 text-body sm:px-6"
            >
              <span className="text-critical-ink">Couldn’t load the fields you can filter by.</span>
              <Button size="sm" onClick={() => void metaQuery.refetch()}>
                Try again
              </Button>
            </div>
          ) : (
            <ListFilterBuilder
              fields={fields}
              applied={builder}
              onApply={(next) => setBuilder(next)}
              onClose={() => setPanelOpen(false)}
            />
          )}
        </div>
      )}

      {/* --- Results ------------------------------------------------------------------ */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        {listQuery.isFetching && !listQuery.isLoading && (
          <div aria-hidden className="absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse bg-accent/60" />
        )}
        <div className={cn('@container scrollbar-slim min-h-0 flex-1 overflow-auto', selected.size > 0 && 'pb-20')}>
          {viewMissing ? (
            <EmptyState
              icon={<Inbox className="size-8" />}
              title="That view no longer exists"
              description="It may have been deleted, or it was private to somebody else."
              action={
                <Button asChild>
                  <Link to="/tickets">Show all tickets</Link>
                </Button>
              }
            />
          ) : viewId !== null && viewsQuery.isError ? (
            <ErrorState
              title="Couldn’t load this view"
              message={viewsQuery.error.message}
              onRetry={() => void viewsQuery.refetch()}
            />
          ) : listQuery.isError ? (
            SERVER_FILTER_ERRORS.has(listQuery.error.code) ? (
              <FilterErrorState
                message={listQuery.error.message}
                onEdit={() => setPanelOpen(true)}
                onClear={reset}
                inView={activeView !== null}
              />
            ) : (
              <ErrorState
                message={listQuery.error.message}
                requestId={listQuery.error.requestId}
                onRetry={() => void listQuery.refetch()}
              />
            )
          ) : tickets.length === 0 && !listQuery.isFetching && !viewPending && meta && meta.total === 0 ? (
            <ListEmptyState
              trash={trash}
              view={activeView}
              changed={changed}
              filtered={anythingApplied}
              onReset={reset}
              canCreate={can('ticket.create')}
            />
          ) : (
            <TicketTable
              tickets={tickets}
              mode={trash ? 'trash' : 'agent'}
              columns={columns}
              sort={explicitSort}
              defaultSort={implicitSort}
              onSortChange={(next: SortSpec[]) => update({ sort: serialiseSort(next) })}
              selectable={selectable}
              selected={selected}
              onSelectedChange={setSelected}
              loading={listQuery.isLoading || viewPending || tickets.length === 0}
              statusChoices={statusChoices}
              canAssign={can('ticket.assign')}
              onRestore={canTrash ? (ticket) => void restoreOne(ticket) : undefined}
              restoring={restoring}
              density={density}
            />
          )}
        </div>

        <BulkActionBar
          mode={trash ? 'trash' : 'active'}
          selected={[...selected]}
          visibleNumbers={tickets.map((ticket) => ticket.number)}
          onClear={() => setSelected(new Set())}
          onSelectOnly={(numbers) => setSelected(new Set(numbers))}
          statuses={statusChoices}
          priorities={choicesFor('priority')}
        />
      </div>

      {meta && meta.total > 0 && (
        <ListPagination
          meta={meta}
          shown={tickets.length}
          onPage={(next) => update({ page: String(next) }, { keepPage: true })}
          perPage={perPage}
          onPerPage={(next) => {
            setPerPage(next);
            writePerPage(next);
            update({ page: null });
          }}
          updatedAt={listQuery.dataUpdatedAt}
          order={describeSort(requestSort, fieldsByKey)}
        />
      )}

      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        snapshot={snapshot}
        onSaved={(id) => {
          setColumnOverride(null);
          navigate(`/tickets?view=${id}`);
        }}
      />
    </div>
  );
}

function QuickChips({
  chips,
  active,
  onToggle,
  counts,
}: {
  chips: QuickFilter[];
  active: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** How many tickets each chip would match here; a chip without one shows no number. */
  counts?: ReadonlyMap<string, number>;
}) {
  const dimensions = [...new Set(chips.map((chip) => chip.dimension))];
  return (
    <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1.5" aria-label="Quick filters" role="group">
      {dimensions.map((dimension) => (
        <div
          key={dimension}
          role="group"
          aria-label={QUICK_DIMENSION_LABELS[dimension]}
          className="flex flex-wrap items-center gap-1"
        >
          {chips
            .filter((chip) => chip.dimension === dimension)
            .map((chip) => {
              const on = active.has(chip.id);
              const count = counts?.get(chip.id);
              return (
                <button
                  key={chip.id}
                  type="button"
                  onClick={() => onToggle(chip.id)}
                  aria-pressed={on}
                  // One phrase for assistive technology, as the view tabs say theirs.
                  aria-label={count === undefined ? undefined : `${chip.label}, ${pluralise(count, 'ticket')}`}
                  title={dimension === 'sla' ? 'Open tickets only' : undefined}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-mini font-medium transition-colors',
                    on
                      ? 'border-accent-line bg-accent-soft text-accent-ink'
                      : 'border-line bg-surface text-ink-muted hover:border-line-strong hover:text-ink',
                  )}
                >
                  {chip.label}
                  {count !== undefined && (
                    <span aria-hidden className={cn('font-normal tabular-nums', on ? 'text-accent-ink/80' : 'text-ink-subtle')}>
                      {count.toLocaleString()}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      ))}
    </div>
  );
}

function Chip({ label, onRemove }: { label: string; onRemove: () => void }) {
  return (
    <Badge tone="accent" className="max-w-full pe-0.5 ps-2">
      <span dir="auto" className="max-w-72 truncate" title={label}>
        {label}
      </span>
      <button
        type="button"
        onClick={onRemove}
        aria-label={`Remove filter: ${label}`}
        className="ms-0.5 rounded-full p-0.5 transition-colors hover:bg-accent-line/50"
      >
        <X aria-hidden className="size-2.5" />
      </button>
    </Badge>
  );
}

function SaveViewControl({
  view,
  blocked,
  saving,
  onSaveNew,
  onSaveChanges,
}: {
  /** The view changes can be saved back to, when there is one. */
  view: SavedView | null;
  /** Why the changes cannot be saved back to it, when they cannot. */
  blocked: string | null;
  saving: boolean;
  onSaveNew: () => void;
  onSaveChanges: (view: SavedView) => void;
}) {
  if (!view) {
    return (
      <Button variant="secondary" onClick={onSaveNew}>
        <Save aria-hidden className="size-3.5" />
        Save view
      </Button>
    );
  }
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="secondary" loading={saving}>
          {!saving && <Save aria-hidden className="size-3.5" />}
          Save view
          <ChevronDown aria-hidden className="size-3" />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={6}
          className="animate-pop z-50 min-w-56 rounded-card border border-line bg-raised p-1 shadow-float"
        >
          <DropdownMenu.Item
            disabled={blocked !== null}
            onSelect={() => onSaveChanges(view)}
            className="flex cursor-pointer flex-col items-start rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken data-disabled:cursor-default"
          >
            <span className={cn(blocked && 'text-ink-faint')}>
              Save changes to “<span dir="auto">{view.name}</span>”
            </span>
            {blocked && <span className="mt-0.5 max-w-64 text-mini text-ink-muted">{blocked}</span>}
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onSelect={onSaveNew}
            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-body text-ink outline-none data-highlighted:bg-sunken"
          >
            Save as a new view…
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * A filter the server refused. "Try again" would only send it again, so the
 * way out offered is to change it or drop it.
 */
function FilterErrorState({
  message,
  onEdit,
  onClear,
  inView,
}: {
  message: string;
  onEdit: () => void;
  onClear: () => void;
  inView: boolean;
}) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center px-6 py-14 text-center">
      <p className="text-sm font-medium text-critical-ink">This filter can’t be applied</p>
      <p className="mt-1 max-w-md text-mini text-ink-muted" dir="auto">
        {message}
      </p>
      <div className="mt-4 flex gap-2">
        <Button onClick={onEdit}>Edit the filter</Button>
        <Button variant="ghost" onClick={onClear}>
          {inView ? 'Undo changes' : 'Clear all filters'}
        </Button>
      </div>
    </div>
  );
}

function ListEmptyState({
  trash,
  view,
  changed,
  filtered,
  onReset,
  canCreate,
}: {
  trash: boolean;
  view: SavedView | null;
  changed: boolean;
  filtered: boolean;
  onReset: () => void;
  canCreate: boolean;
}) {
  if (trash) {
    return (
      <EmptyState
        icon={<Trash2 className="size-8" />}
        title={filtered ? 'Nothing in the trash matches' : 'The trash is empty'}
        description={filtered ? 'Try a different search or filter.' : 'Deleted tickets appear here until somebody restores them.'}
        action={filtered ? <Button onClick={onReset}>Clear all</Button> : undefined}
      />
    );
  }
  if (view && !changed) {
    return (
      <EmptyState
        icon={<Inbox className="size-8" />}
        title={`Nothing in “${view.name}” right now`}
        description={view.description ?? 'Tickets that match this view will appear here.'}
      />
    );
  }
  if (filtered || changed) {
    return (
      <EmptyState
        icon={<Inbox className="size-8" />}
        title="No tickets match"
        description="Try removing a filter, or searching for something broader."
        action={<Button onClick={onReset}>{view ? 'Undo changes' : 'Clear all'}</Button>}
      />
    );
  }
  return (
    <EmptyState
      icon={<Inbox className="size-8" />}
      title="No tickets yet"
      description="Tickets raised by email, the portal or the API appear here."
      action={
        canCreate ? (
          <Button variant="primary" asChild>
            <Link to="/tickets/new">New ticket</Link>
          </Button>
        ) : undefined
      }
    />
  );
}

function ColumnPicker({
  selected,
  onChange,
  onReset,
  scope,
}: {
  selected: ColumnKey[];
  onChange: (keys: ColumnKey[]) => void;
  onReset: () => void;
  scope: 'view' | 'mine';
}) {
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <Button variant="secondary">
          <Columns3 aria-hidden className="size-3.5" />
          Columns
        </Button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="end"
          sideOffset={6}
          className="animate-pop z-50 w-56 rounded-card border border-line bg-raised p-1.5 shadow-float"
        >
          <p className="px-1.5 pb-1 text-mini font-medium text-ink-muted">
            {scope === 'view' ? 'Columns in this view' : 'Your columns'}
          </p>
          {ALL_COLUMNS.map((column) => {
            const checked = selected.includes(column.key);
            // Subject is what identifies a row; hiding it would leave an
            // unreadable table, so it is not offered as removable.
            const locked = column.key === 'subject';
            return (
              <label
                key={column.key}
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-1.5 py-1.5 text-body text-ink',
                  locked ? 'cursor-default opacity-60' : 'hover:bg-sunken',
                )}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={locked}
                  onChange={() =>
                    onChange(
                      sanitiseColumnKeys(
                        checked ? selected.filter((key) => key !== column.key) : [...selected, column.key],
                      ),
                    )
                  }
                  className="size-3.5 accent-[var(--color-accent)]"
                />
                {column.label}
              </label>
            );
          })}
          <div className="mt-1 border-t border-line pt-1">
            <button
              type="button"
              onClick={onReset}
              className="w-full rounded px-1.5 py-1.5 text-start text-mini text-ink-muted hover:bg-sunken hover:text-ink"
            >
              {scope === 'view' ? 'Back to the view’s columns' : 'Reset to the default columns'}
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

// ---------------------------------------------------------------------------
// The requester's list
// ---------------------------------------------------------------------------

/**
 * "My requests": what somebody has asked the desk for, and where each has got
 * to, in their words — "Awaiting your reply", not "Pending". No SLA timers, no
 * queues, no bulk selection, no internal jargon: none of it is theirs to act
 * on. The server limits what they see to their own requests regardless.
 */
function RequesterTicketList() {
  const { can } = useSession();
  const [searchParams] = useSearchParams();
  const update = useParamUpdater();
  const searchRef = useRef<HTMLInputElement>(null);
  useSlashToSearch(searchRef);

  const show = parseRequesterShow(searchParams.get('show'));
  const q = searchParams.get('q') ?? '';
  const page = parsePage(searchParams.get('page'));
  const filter = REQUESTER_SHOW[show].filter;
  const search = q.trim();

  const [searchDraft, setSearchDraft, flushSearch] = useDebouncedSearch(q, (next) => update({ q: next || null }));

  const listQuery = useQuery<ListResponse<TicketSummary>, ApiError>({
    queryKey: ticketKeys.list({ requester: true, show, q: search, page }),
    queryFn: () =>
      post<ListResponse<TicketSummary>>('/tickets/search', {
        ...(filter ? { filter } : {}),
        ...(search ? { q: search } : {}),
        page,
        per_page: 30,
      }),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });

  const tickets = listQuery.data?.data ?? [];
  const meta = listQuery.data?.meta;

  useEffect(() => {
    if (!meta || listQuery.isPlaceholderData) return;
    if (meta.total > 0 && meta.page > meta.total_pages) update({ page: String(meta.total_pages) }, { keepPage: true });
  }, [meta, listQuery.isPlaceholderData, update]);

  const reportAction = can('ticket.create') ? (
    <Button variant="primary" asChild>
      <Link to="/tickets/new">
        <Plus aria-hidden className="size-3.5" />
        Report an issue
      </Link>
    </Button>
  ) : null;

  let body: ReactNode;
  if (listQuery.isError) {
    body = (
      <ErrorState
        title="Couldn’t load your requests"
        message={listQuery.error.message}
        requestId={listQuery.error.requestId}
        onRetry={() => void listQuery.refetch()}
      />
    );
  } else if (tickets.length === 0 && !listQuery.isFetching && meta && meta.total === 0) {
    body = search ? (
      <EmptyState
        icon={<Search className="size-8" />}
        title="Nothing matches that search"
        description="Try other words, or look under All."
        action={<Button onClick={() => update({ q: null })}>Clear the search</Button>}
      />
    ) : show === 'resolved' ? (
      <EmptyState icon={<Inbox className="size-8" />} title="Nothing resolved yet" description="Requests the desk has finished appear here." />
    ) : (
      <EmptyState
        icon={<Inbox className="size-8" />}
        title={show === 'open' ? 'Nothing open' : 'You haven’t asked for anything yet'}
        description="When you report an issue or ask for something, you can follow it here."
        action={reportAction ?? undefined}
      />
    );
  } else {
    body = (
      <TicketTable
        tickets={tickets}
        mode="requester"
        columns={[]}
        sort={[]}
        onSortChange={() => undefined}
        selectable={false}
        selected={EMPTY_SELECTION}
        onSelectedChange={() => undefined}
        loading={listQuery.isLoading || tickets.length === 0}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="My requests"
        description="Everything you have asked the IT desk for, and where each one has got to."
        actions={reportAction}
      >
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <nav aria-label="Which requests" className="flex items-center gap-1">
            {(Object.keys(REQUESTER_SHOW) as RequesterShow[]).map((key) => (
              <Link
                key={key}
                to={key === 'open' ? '/tickets' : `/tickets?show=${key}`}
                aria-current={show === key ? 'page' : undefined}
                className={cn(
                  'inline-flex h-7 items-center rounded-control px-2.5 text-body transition-colors',
                  show === key ? 'bg-accent-soft font-medium text-accent-ink' : 'text-ink-muted hover:bg-sunken hover:text-ink',
                )}
              >
                {REQUESTER_SHOW[key].label}
              </Link>
            ))}
          </nav>
          <SearchBox
            inputRef={searchRef}
            value={searchDraft}
            onChange={setSearchDraft}
            onFlush={flushSearch}
            placeholder="Search your requests"
            label="Search your requests"
          />
        </div>
      </PageHeader>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {listQuery.isFetching && !listQuery.isLoading && (
          <div aria-hidden className="absolute inset-x-0 top-0 z-20 h-0.5 animate-pulse bg-accent/60" />
        )}
        <div className="@container scrollbar-slim min-h-0 flex-1 overflow-auto">{body}</div>
      </div>

      {meta && meta.total > 0 && (
        <ListPagination
          meta={meta}
          shown={tickets.length}
          onPage={(next) => update({ page: String(next) }, { keepPage: true })}
          perPage={30}
          updatedAt={listQuery.dataUpdatedAt}
        />
      )}
    </div>
  );
}

const EMPTY_SELECTION: ReadonlySet<number> = new Set();
